import { useState, useEffect } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { defaultForm } from "../constants/defaultForm";

const usePageBuilder = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();
  const { pageId: routePageId } = useParams();
  const [searchParams] = useSearchParams();
  const cloneFromId = searchParams.get("cloneFrom");

  // ─── Core State ───
  const [savedPageId, setSavedPageId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(defaultForm);

  // ─── Modal State ───
  const [overviewSection, setOverviewSection] = useState(null);
  const [detailIndex, setDetailIndex] = useState(null);
  const [detailDraft, setDetailDraft] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  const [modalDraft, setModalDraft] = useState(null);

  const effectiveId = routePageId || savedPageId;

  // ─── Fetch existing/cloned page ───
  useEffect(() => {
    const targetId = routePageId || cloneFromId;
    if (!targetId) return;

    const fetchPage = async () => {
      try {
        const res = await axios.get(`${apiUrl}/pages/id/${targetId}`);
        const d = res.data.data;

        const isCloning = !!cloneFromId && !routePageId;
        const newTitle = isCloning
          ? `${d.title || "Untitled"} (Copy)`
          : d.title || "";
        const newSlug = isCloning ? "" : d.slug || "";

        setForm({
          meta: { title: newTitle, slug: newSlug },
          hero: d.hero || defaultForm.hero,
          features: d.features || defaultForm.features,
          stats: d.stats || defaultForm.stats,
          caseStudies: d.caseStudies || defaultForm.caseStudies,
          faq: d.faq || defaultForm.faq,
          team: d.team || defaultForm.team,
          testimonials: d.testimonials || defaultForm.testimonials,
          pricing: d.pricing || defaultForm.pricing,
          footer: {
            description: d.footer?.description || "",
            contactEmail: d.footer?.email || "",
            contactPhone: d.footer?.phone || "",
            usefulLinks: d.footer?.links || [],
          },
          contact: {
            heading: d.contactCta?.heading || "",
            subheading: d.contactCta?.subheading || "",
            submitBtnText: d.contactCta?.buttonText || "",
          },
        });
      } catch (err) {
        console.error("Failed to fetch page:", err);
      }
    };
    fetchPage();
  }, [routePageId, cloneFromId, apiUrl]);

  // ─── Auto-Save to Backend ───
  const saveToBackend = async (formData) => {
    if (!effectiveId) return null;

    try {
      const payload = {
        title: formData.meta.title,
        slug: formData.meta.slug,
        isPublished: false, // Draft mode
        hero: formData.hero,
        features: formData.features,
        stats: formData.stats,
        caseStudies: formData.caseStudies,
        faq: formData.faq,
        team: formData.team,
        testimonials: formData.testimonials,
        pricing: formData.pricing,
        contactCta: {
          heading: formData.contact.heading,
          subheading: formData.contact.subheading,
          buttonText: formData.contact.submitBtnText,
        },
        footer: {
          description: formData.footer.description,
          email: formData.footer.contactEmail,
          phone: formData.footer.contactPhone,
          links: formData.footer.usefulLinks,
        },
      };

      const res = await axios.put(`${apiUrl}/pages/${effectiveId}`, payload);
      console.log("✅ Auto-saved to backend");
      return res.data.data;
    } catch (error) {
      console.error("⚠️ Auto-save failed:", error);
      return null;
    }
  };

  // ─── Section Modal Handlers ───
  const openSectionModal = (key) => {
    setEditingSection(key);
    setModalDraft(form[key]);
  };

  const saveSectionModal = () => {
    setForm((prev) => ({ ...prev, [editingSection]: modalDraft }));
    setEditingSection(null);
    setModalDraft(null);

    // Auto-save after 1 second debounce
    setTimeout(() => {
      saveToBackend({ ...form, [editingSection]: modalDraft });
    }, 1000);
  };

  const closeSectionModal = () => {
    setEditingSection(null);
    setModalDraft(null);
  };

  // ─── Save Page (Manual) ───
  const handleSave = async (publish = false, redirect = true) => {
    try {
      setSaving(true);
      const payload = {
        title: form.meta.title,
        slug: form.meta.slug,
        isPublished: publish,
        hero: form.hero,
        features: form.features,
        stats: form.stats,
        caseStudies: form.caseStudies,
        faq: form.faq,
        team: form.team,
        testimonials: form.testimonials,
        pricing: form.pricing,
        contactCta: {
          heading: form.contact.heading,
          subheading: form.contact.subheading,
          buttonText: form.contact.submitBtnText,
        },
        footer: {
          description: form.footer.description,
          email: form.footer.contactEmail,
          phone: form.footer.contactPhone,
          links: form.footer.usefulLinks,
        },
      };

      if (effectiveId) {
        await axios.put(`${apiUrl}/pages/${effectiveId}`, payload);
      } else {
        const res = await axios.post(`${apiUrl}/pages/create`, payload);
        setSavedPageId(res.data.data._id);
      }

      if (redirect) navigate("/pages");
    } catch (err) {
      console.error(err);
      alert("Failed to save page");
    } finally {
      setSaving(false);
    }
  };

  return {
    // IDs & Navigation
    apiUrl,
    navigate,
    routePageId,
    cloneFromId,
    effectiveId,

    // Core State
    form,
    setForm,
    saving,
    setSaving,
    savedPageId,

    // Modal State
    editingSection,
    setEditingSection,
    modalDraft,
    setModalDraft,
    openSectionModal,
    saveSectionModal,
    closeSectionModal,
    overviewSection,
    setOverviewSection,
    detailIndex,
    setDetailIndex,
    detailDraft,
    setDetailDraft,

    // Actions
    handleSave,
    saveToBackend,
  };
};

export default usePageBuilder;
